/**
 * Data Service Layer - Bridge Pattern Integration
 * 
 * This service now uses the Bridge Pattern to abstract data persistence.
 * All data operations are delegated to the active data provider (LocalStorage or Firestore).
 * 
 * The service maintains backward compatibility with existing code while providing
 * the flexibility to switch between data providers easily.
 * 
 * To switch from LocalStorage to Firestore:
 * 1. Go to dataProviders/DataProviderFactory.ts
 * 2. Change DATA_PROVIDER_TYPE from 'localStorage' to 'firestore'
 * 3. Implement Firestore methods in FirestoreDataProvider.ts
 * 4. That's it! No changes needed here or in any components
 */

import {
  AppState,
  Organization,
  User,
  Project,
  CostItem,
  Requirement,
  ProjectNdaSignature,
  AccountingRequirement,
  AccountingMilestone,
  CapitalLedgerEntry,
  Task,
  Epic,
  Story,
  Sprint,
  Retrospective,
  Report,
  Comment,
  Notification,
  UserRequest,
  StandupNote,
  PinnedMessage,
  NewsFeedPost,
  NewsFeedComment,
} from '../types';
import { getDataProvider } from '../providers/data';

// ============================================================================
// BRIDGE PATTERN: All operations now delegate to the data provider
// ============================================================================

/**
 * Get the active data provider
 * This is the bridge to the concrete implementation
 */
const provider = getDataProvider();

// ============================================================================
// STATE MANAGEMENT
// ============================================================================

/**
 * Load application state
 * Delegates to the active provider (LocalStorage or Firestore)
 */
export const loadState = async (email: string): Promise<AppState> => {
  return provider.loadState(email);
};

/**
 * Save application state
 * Delegates to the active provider
 */
export const saveState = (state: AppState): void => {
  provider.saveState(state);
};

/**
 * Clear all data (use with caution!)
 */
export const clearAllData = async (): Promise<void> => {
  return provider.clearAllData();
};

// ============================================================================
// ORGANIZATION OPERATIONS
// ============================================================================

export const getOrganizations = (): Promise<Organization[]> => {
  return provider.getOrganizations();
};

export const getOrganizationById = (orgId: string): Promise<Organization | null> => {
  return provider.getOrganizationById(orgId);
};

export const addOrganization = (org: Organization): Promise<void> => {
  return provider.addOrganization(org);
};

export const updateOrganization = (orgId: string, updates: Partial<Organization>): Promise<void> => {
  return provider.updateOrganization(orgId, updates);
};

export const deleteOrganization = (orgId: string): Promise<void> => {
  return provider.deleteOrganization(orgId);
};

// ============================================================================
// ORGANIZATION CONTEXT (for backward compatibility)
// ============================================================================

let currentOrgId: string | null = null;

export const setCurrentOrganization = (orgId: string): void => {
  currentOrgId = orgId;
  console.log(`[DataService] Current organization set to: ${orgId}`);
};

export const getCurrentOrganization = (): string | null => {
  return currentOrgId;
};

// ============================================================================
// USER OPERATIONS
// ============================================================================

export const getUsers = (orgId: string): Promise<User[]> => {
  return provider.getUsers(orgId);
};

export const getUserById = (userId: string): Promise<User | null> => {
  return provider.getUserById(userId);
};

export const addUser = (user: User): Promise<void> => {
  return provider.addUser(user);
};

export const updateUser = (userId: string, updates: Partial<User>): Promise<void> => {
  return provider.updateUser(userId, updates);
};

export const deleteUser = (userId: string): Promise<void> => {
  return provider.deleteUser(userId);
};

export const getUsersByOrganization = (orgId: string): Promise<User[]> => {
  return provider.getUsersByOrganization(orgId);
};

// ============================================================================
// PROJECT OPERATIONS
// ============================================================================

export const getProjects = (orgId: string): Promise<Project[]> => {
  return provider.getProjects(orgId);
};

export const getProjectById = (projectId: string): Promise<Project | null> => {
  return provider.getProjectById(projectId);
};

export const addProject = (orgId: string, project: Project): Promise<void> => {
  return provider.addProject(orgId, project);
};

export const updateProject = (orgId: string, projectId: string, updates: Partial<Project>): Promise<void> => {
  return provider.updateProject(orgId, projectId, updates);
};

export const deleteProject = (orgId: string, projectId: string): Promise<void> => {
  return provider.deleteProject(orgId, projectId);
};

export const getProjectsByOrganization = (orgId: string): Promise<Project[]> => {
  return provider.getProjectsByOrganization(orgId);
};

export const getProjectCostItems = (orgId: string, projectId: string): Promise<CostItem[]> => {
  return provider.getProjectCostItems(orgId, projectId);
};

export const setProjectCostItems = (
  orgId: string,
  projectId: string,
  items: CostItem[]
): Promise<void> => {
  return provider.setProjectCostItems(orgId, projectId, items);
};

export const getProjectRequirements = (
  orgId: string,
  projectId: string
): Promise<Requirement[]> => {
  return provider.getProjectRequirements(orgId, projectId);
};

export const setProjectRequirements = (
  orgId: string,
  projectId: string,
  requirements: Requirement[]
): Promise<void> => {
  return provider.setProjectRequirements(orgId, projectId, requirements);
};

export const getProjectNdaSignatures = (
  orgId: string,
  projectId: string
): Promise<ProjectNdaSignature[]> => {
  return provider.getProjectNdaSignatures(orgId, projectId);
};

export const setProjectNdaSignatures = (
  orgId: string,
  projectId: string,
  signatures: ProjectNdaSignature[]
): Promise<void> => {
  return provider.setProjectNdaSignatures(orgId, projectId, signatures);
};

export const getProjectAccountingRequirements = (
  orgId: string,
  projectId: string
): Promise<AccountingRequirement[]> => {
  return provider.getProjectAccountingRequirements(orgId, projectId);
};

export const setProjectAccountingRequirements = (
  orgId: string,
  projectId: string,
  requirements: AccountingRequirement[]
): Promise<void> => {
  return provider.setProjectAccountingRequirements(orgId, projectId, requirements);
};

export const getProjectAccountingMilestones = (
  orgId: string,
  projectId: string
): Promise<AccountingMilestone[]> => {
  return provider.getProjectAccountingMilestones(orgId, projectId);
};

export const setProjectAccountingMilestones = (
  orgId: string,
  projectId: string,
  milestones: AccountingMilestone[]
): Promise<void> => {
  return provider.setProjectAccountingMilestones(orgId, projectId, milestones);
};

export const getProjectCapitalLedgerEntries = (
  orgId: string,
  projectId: string
): Promise<CapitalLedgerEntry[]> => {
  return provider.getProjectCapitalLedgerEntries(orgId, projectId);
};

export const setProjectCapitalLedgerEntries = (
  orgId: string,
  projectId: string,
  entries: CapitalLedgerEntry[]
): Promise<void> => {
  return provider.setProjectCapitalLedgerEntries(orgId, projectId, entries);
};

// ============================================================================
// TASK OPERATIONS
// ============================================================================

export const getTasks = (orgId: string): Promise<Task[]> => {
  return provider.getTasks(orgId);
};

export const getTaskById = (taskId: string): Promise<Task | null> => {
  return provider.getTaskById(taskId);
};

export const getTasksByProject = (projectId: string): Promise<Task[]> => {
  return provider.getTasksByProject(projectId);
};

export const getTasksByEpic = (epicId: string): Promise<Task[]> => {
  return provider.getTasksByEpic(epicId);
};

export const getTasksBySprint = (sprintId: string): Promise<Task[]> => {
  return provider.getTasksBySprint(sprintId);
};

export const getTasksByAssignee = (userId: string): Promise<Task[]> => {
  return provider.getTasksByAssignee(userId);
};

export const addTask = (task: Task): Promise<void> => {
  return provider.addTask(task);
};

export const updateTask = (orgId: string, taskId: string, updates: Partial<Task>): Promise<void> => {
  return provider.updateTask(orgId, taskId, updates);
};

export const deleteTask = (orgId: string, taskId: string): Promise<void> => {
  return provider.deleteTask(orgId, taskId);
};

// ============================================================================
// EPIC OPERATIONS
// ============================================================================

export const getEpics = (orgId: string): Promise<Epic[]> => {
  return provider.getEpics(orgId);
};

export const getEpicById = (epicId: string): Promise<Epic | null> => {
  return provider.getEpicById(epicId);
};

export const getEpicsByProject = (projectId: string): Promise<Epic[]> => {
  return provider.getEpicsByProject(projectId);
};

export const addEpic = (epic: Epic): Promise<void> => {
  return provider.addEpic(epic);
};

export const updateEpic = (orgId: string, epicId: string, updates: Partial<Epic>): Promise<void> => {
  return provider.updateEpic(orgId, epicId, updates);
};

export const deleteEpic = (orgId: string, epicId: string): Promise<void> => {
  return provider.deleteEpic(orgId, epicId);
};

// ============================================================================
// STORY OPERATIONS
// ============================================================================

export const getStories = (orgId: string): Promise<Story[]> => {
  return provider.getStories(orgId);
};

export const getStoryById = (storyId: string): Promise<Story | null> => {
  return provider.getStoryById(storyId);
};

export const getStoriesByProject = (projectId: string): Promise<Story[]> => {
  return provider.getStoriesByProject(projectId);
};

export const addStory = (story: Story): Promise<void> => {
  return provider.addStory(story);
};

export const updateStory = (orgId: string, storyId: string, updates: Partial<Story>): Promise<void> => {
  return provider.updateStory(orgId, storyId, updates);
};

export const deleteStory = (orgId: string, storyId: string): Promise<void> => {
  return provider.deleteStory(orgId, storyId);
};

// ============================================================================
// SPRINT OPERATIONS
// ============================================================================

export const getSprints = (orgId: string): Promise<Sprint[]> => {
  return provider.getSprints(orgId);
};

export const getSprintById = (sprintId: string): Promise<Sprint | null> => {
  return provider.getSprintById(sprintId);
};

export const getSprintsByProject = (projectId: string): Promise<Sprint[]> => {
  return provider.getSprintsByProject(projectId);
};

export const addSprint = (sprint: Sprint): Promise<void> => {
  return provider.addSprint(sprint);
};

export const updateSprint = (orgId: string, sprintId: string, updates: Partial<Sprint>): Promise<void> => {
  return provider.updateSprint(orgId, sprintId, updates);
};

export const deleteSprint = (orgId: string, sprintId: string): Promise<void> => {
  return provider.deleteSprint(orgId, sprintId);
};

// ============================================================================
// RETROSPECTIVE OPERATIONS
// ============================================================================

export const getRetrospectives = (orgId: string): Promise<Retrospective[]> => {
  return provider.getRetrospectives(orgId);
};

export const getRetrospectiveById = (retroId: string): Promise<Retrospective | null> => {
  return provider.getRetrospectiveById(retroId);
};

export const getRetrospectivesByProject = (projectId: string): Promise<Retrospective[]> => {
  return provider.getRetrospectivesByProject(projectId);
};

export const addRetrospective = (retro: Retrospective): Promise<void> => {
  return provider.addRetrospective(retro);
};

export const updateRetrospective = (orgId: string, retroId: string, updates: Partial<Retrospective>): Promise<void> => {
  return provider.updateRetrospective(orgId, retroId, updates);
};

export const deleteRetrospective = (orgId: string, retroId: string): Promise<void> => {
  return provider.deleteRetrospective(orgId, retroId);
};

// ============================================================================
// REPORT OPERATIONS
// ============================================================================

export const getReports = (orgId: string): Promise<Report[]> => {
  return provider.getReports(orgId);
};

export const getReportById = (reportId: string): Promise<Report | null> => {
  return provider.getReportById(reportId);
};

export const getReportsByProject = (projectId: string): Promise<Report[]> => {
  return provider.getReportsByProject(projectId);
};

export const addReport = (report: Report): Promise<void> => {
  return provider.addReport(report);
};

export const updateReport = (orgId: string, reportId: string, updates: Partial<Report>): Promise<void> => {
  return provider.updateReport(orgId, reportId, updates);
};

export const deleteReport = (orgId: string, reportId: string): Promise<void> => {
  return provider.deleteReport(orgId, reportId);
};

// ============================================================================
// NOTIFICATION OPERATIONS
// ============================================================================

export const getNotifications = (userId: string): Promise<Notification[]> => {
  return provider.getNotifications(userId);
};

export const addNotification = (notification: Notification): Promise<void> => {
  return provider.addNotification(notification);
};

export const updateNotification = (notificationId: string, updates: Partial<Notification>): Promise<void> => {
  return provider.updateNotification(notificationId, updates);
};

export const deleteNotification = (notificationId: string): Promise<void> => {
  return provider.deleteNotification(notificationId);
};

export const markNotificationAsRead = (notificationId: string): Promise<void> => {
  return provider.markNotificationAsRead(notificationId);
};

// ============================================================================
// USER REQUEST OPERATIONS
// ============================================================================

export const getUserRequests = (orgId: string): Promise<UserRequest[]> => {
  return provider.getUserRequests(orgId);
};

export const getUserRequestsByTask = (taskId: string): Promise<UserRequest[]> => {
  return provider.getUserRequestsByTask(taskId);
};

export const addUserRequest = (request: UserRequest): Promise<void> => {
  return provider.addUserRequest(request);
};

export const updateUserRequest = (requestId: string, updates: Partial<UserRequest>): Promise<void> => {
  return provider.updateUserRequest(requestId, updates);
};

export const deleteUserRequest = (requestId: string): Promise<void> => {
  return provider.deleteUserRequest(requestId);
};

// ============================================================================
// STANDUP NOTE OPERATIONS
// ============================================================================

export const getStandupNotes = (orgId: string): Promise<StandupNote[]> => {
  return provider.getStandupNotes(orgId);
};

export const getStandupNotesByUser = (userId: string): Promise<StandupNote[]> => {
  return provider.getStandupNotesByUser(userId);
};

export const getStandupNotesBySprint = (sprintId: string): Promise<StandupNote[]> => {
  return provider.getStandupNotesBySprint(sprintId);
};

export const addStandupNote = (note: StandupNote): Promise<void> => {
  return provider.addStandupNote(note);
};

export const updateStandupNote = (noteId: string, updates: Partial<StandupNote>): Promise<void> => {
  return provider.updateStandupNote(noteId, updates);
};

export const deleteStandupNote = (noteId: string): Promise<void> => {
  return provider.deleteStandupNote(noteId);
};

// ============================================================================
// COMMENT OPERATIONS
// ============================================================================

export const addCommentToEntity = (
  entityType: 'task' | 'epic' | 'story' | 'project' | 'sprint' | 'retrospective' | 'report',
  entityId: string,
  comment: Comment
): Promise<void> => {
  return provider.addCommentToEntity(entityType, entityId, comment);
};

export const updateCommentInEntity = (
  entityType: 'task' | 'epic' | 'story' | 'project' | 'sprint' | 'retrospective' | 'report',
  entityId: string,
  commentId: string,
  updates: Partial<Comment>
): Promise<void> => {
  return provider.updateCommentInEntity(entityType, entityId, commentId, updates);
};

export const deleteCommentFromEntity = (
  entityType: 'task' | 'epic' | 'story' | 'project' | 'sprint' | 'retrospective' | 'report',
  entityId: string,
  commentId: string
): Promise<void> => {
  return provider.deleteCommentFromEntity(entityType, entityId, commentId);
};

// ============================================================================
// PINNED MESSAGE OPERATIONS
// ============================================================================

export const addPinnedMessage = (
  entityType: 'project' | 'sprint' | 'task',
  entityId: string,
  message: PinnedMessage
): Promise<void> => {
  return provider.addPinnedMessage(entityType, entityId, message);
};

export const updatePinnedMessage = (
  entityType: 'project' | 'sprint' | 'task',
  entityId: string,
  messageId: string,
  updates: Partial<PinnedMessage>
): Promise<void> => {
  return provider.updatePinnedMessage(entityType, entityId, messageId, updates);
};

export const deletePinnedMessage = (
  entityType: 'project' | 'sprint' | 'task',
  entityId: string,
  messageId: string
): Promise<void> => {
  return provider.deletePinnedMessage(entityType, entityId, messageId);
};

// ============================================================================
// NEWS FEED OPERATIONS
// ============================================================================

export const getOrgNewsFeedPosts = (orgId: string): Promise<NewsFeedPost[]> =>
  provider.getOrgNewsFeedPosts(orgId);

export const getProjectNewsFeedPosts = (orgId: string, projectId: string): Promise<NewsFeedPost[]> =>
  provider.getProjectNewsFeedPosts(orgId, projectId);

export const addNewsFeedPost = (post: NewsFeedPost): Promise<void> =>
  provider.addNewsFeedPost(post);

export const updateNewsFeedPost = (
  orgId: string,
  postId: string,
  updates: Partial<NewsFeedPost>,
  projectId?: string
): Promise<void> => provider.updateNewsFeedPost(orgId, postId, updates, projectId);

export const deleteNewsFeedPost = (
  orgId: string,
  postId: string,
  projectId?: string
): Promise<void> => provider.deleteNewsFeedPost(orgId, postId, projectId);

export const addCommentToNewsFeedPost = (
  orgId: string,
  postId: string,
  comment: NewsFeedComment,
  projectId?: string
): Promise<void> => provider.addCommentToNewsFeedPost(orgId, postId, comment, projectId);

export const addReactionToNewsFeedPost = (
  orgId: string,
  postId: string,
  userId: string,
  emoji: string,
  projectId?: string
): Promise<void> => provider.addReactionToNewsFeedPost(orgId, postId, userId, emoji, projectId);

// ============================================================================
// PROVIDER INFO (for debugging and monitoring)
// ============================================================================

/**
 * Get information about the current data provider
 */
export const getProviderInfo = () => {
  return {
    name: provider.getProviderName(),
    isReady: provider.isReady(),
  };
};

/**
 * Log provider information to console
 */
export const logProviderInfo = async () => {
  const info = getProviderInfo();
  const isReady = await info.isReady;
  console.log('[DataService] Provider Info:', {
    name: info.name,
    ready: isReady,
  });
};
